import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import classNames from "classnames";
import type { MouseEvent } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectTrainingType,
  TrainingType,
} from "../../app/slices/TrainingSlice";
import {
  SearchResultData,
  selectActiveFilter,
  selectAllFilters,
  selectFilteredSearchResults,
  selectQuery,
  setActiveFilter,
  setQuery,
  setSearchResults,
} from "../../app/slices/SearchResultsSlice";
import AdvancedSearchResultField from "./AdvancedSearchResultField";
import { useUpdateSearchResults } from "./AdvancedSearchList.hook";
import "./AdvancedSearchOverlay.css";

interface AdvancedSearchOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

function getGroupLabel(result: SearchResultData) {
  return result.takeName ?? result.matchingText;
}

function SearchFilterChips() {
  const dispatch = useDispatch();
  const activeFilter = useSelector(selectActiveFilter);
  const allFilters = useSelector(selectAllFilters);
  const totalCount = Object.values(allFilters).reduce(
    (total, count) => total + count,
    0
  );
  const filterItems = [
    { text: "All", count: totalCount },
    ...Object.entries(allFilters).map(([text, count]) => ({ text, count })),
  ];

  if (filterItems.length <= 1) {
    return null;
  }

  return (
    <div className="advanced-search-overlay__filters">
      {filterItems.map((filter) => (
        <button
          key={filter.text}
          type="button"
          className={classNames(
            "advanced-search-overlay__filter-chip",
            activeFilter === filter.text &&
              "advanced-search-overlay__filter-chip--active"
          )}
          onClick={() => dispatch(setActiveFilter(filter.text))}
        >
          <span>{filter.text}</span>
          <span>{filter.count}</span>
        </button>
      ))}
    </div>
  );
}

function AdvancedSearchOverlayResults(props: { onSelect: () => void }) {
  const { onSelect } = props;
  useUpdateSearchResults();

  const query = useSelector(selectQuery);
  const searchResults = useSelector(selectFilteredSearchResults);
  const [activeIndex, setActiveIndex] = useState(0);
  const resultRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const groupedResults = useMemo(() => {
    const groups = new Map<string, SearchResultData[]>();

    searchResults.forEach((result) => {
      const label = getGroupLabel(result);
      groups.set(label, [...(groups.get(label) ?? []), result]);
    });

    return Array.from(groups.entries());
  }, [searchResults]);
  const activeResult = searchResults[activeIndex];

  useEffect(() => {
    setActiveIndex(0);
  }, [query, searchResults.length]);

  useEffect(() => {
    if (activeIndex >= searchResults.length) {
      setActiveIndex(Math.max(searchResults.length - 1, 0));
    }
  }, [activeIndex, searchResults.length]);

  useEffect(() => {
    if (!activeResult) return;

    resultRefs.current[activeResult.objectId]?.scrollIntoView?.({
      block: "nearest",
    });
  }, [activeResult]);

  const moveActiveResult = useCallback(
    (direction: 1 | -1) => {
      if (searchResults.length === 0) return;

      setActiveIndex((currentIndex) =>
        (currentIndex + direction + searchResults.length) %
        searchResults.length
      );
    },
    [searchResults.length]
  );

  const selectActiveResult = useCallback(() => {
    if (!activeResult) return;

    resultRefs.current[activeResult.objectId]?.click();
  }, [activeResult]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        moveActiveResult(1);
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        moveActiveResult(-1);
      } else if (event.key === "Enter") {
        event.preventDefault();
        selectActiveResult();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [moveActiveResult, selectActiveResult]);

  if (searchResults.length === 0) {
    return (
      <div className="advanced-search-overlay__empty">
        No matching results
      </div>
    );
  }

  return (
    <div className="advanced-search-overlay__results pit-creator-scrollbar">
      {groupedResults.map(([groupLabel, results]) => (
        <section
          className="advanced-search-overlay__group"
          key={groupLabel}
        >
          <div className="advanced-search-overlay__group-header">
            <span>{groupLabel}</span>
            <span>{results.length}</span>
          </div>
          <div className="advanced-search-overlay__group-results">
            {results.map((result) => (
              <AdvancedSearchResultField
                key={result.objectId}
                matchingText={result.matchingText}
                query={query}
                objectId={result.objectId}
                interactionId={result.interactionId}
                takeName={result.takeName}
                interactionType={result.interactionType}
                profilePicture={result.profilePicture}
                variant="overlay"
                onSelect={onSelect}
                isActive={activeResult?.objectId === result.objectId}
                resultRef={(element) => {
                  resultRefs.current[result.objectId] = element;
                }}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

export default function AdvancedSearchOverlay(props: AdvancedSearchOverlayProps) {
  const { isOpen, onClose } = props;
  const dispatch = useDispatch();
  const query = useSelector(selectQuery);
  const trainingType = useSelector(selectTrainingType);
  const unitPlural =
    trainingType === TrainingType.Training360 ? "bubbles" : "takes";
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const focusInput = () => inputRef.current?.focus();
    focusInput();
    const animationFrameId = window.requestAnimationFrame(focusInput);

    return () => window.cancelAnimationFrame(animationFrameId);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const onQueryChange = (value: string) => {
    dispatch(setQuery(value));
    if (value === "" || value === undefined) {
      dispatch(setActiveFilter("All"));
      dispatch(setSearchResults([]));
    }
  };

  const onBackdropClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      aria-label="advanced-search-overlay"
      className="advanced-search-overlay"
      onMouseDown={onBackdropClick}
    >
      <div
        className="advanced-search-overlay__panel"
        role="dialog"
        aria-modal="true"
        aria-label="Advanced search"
      >
        <div className="advanced-search-overlay__header">
          <div className="advanced-search-overlay__input-wrap">
            <SearchIcon fontSize="inherit" />
            <input
              ref={inputRef}
              aria-label="Advanced search input"
              value={query}
              onChange={(event) => onQueryChange(event.target.value)}
              placeholder={`Find anything across all ${unitPlural}...`}
              className="advanced-search-overlay__input"
            />
          </div>
          <button
            type="button"
            className="advanced-search-overlay__close"
            aria-label="Close advanced search"
            onClick={onClose}
          >
            <CloseIcon fontSize="inherit" />
          </button>
        </div>

        {query && <SearchFilterChips />}

        <div className="advanced-search-overlay__body">
          {query ? (
            <AdvancedSearchOverlayResults onSelect={onClose} />
          ) : (
            <div className="advanced-search-overlay__empty">
              Start typing to search {unitPlural} and interactions
            </div>
          )}
        </div>

        <div className="advanced-search-overlay__footer">
          <span>
            <kbd>/</kbd> search
          </span>
          <span>
            <kbd>↑↓</kbd> navigate
          </span>
          <span>
            <kbd>Enter</kbd> jump
          </span>
          <span>
            <kbd>Esc</kbd> close
          </span>
        </div>
      </div>
    </div>
  );
}
